/**
 * Office attachments (PDFs and scans) and the closed-job package to billing. Files are kept
 * forever (never replaced or deleted); they go with the package when the job closes. The iPad
 * never sees them.
 */
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Me } from '@wo/shared';
import { api, errorText } from '../lib/api';
import { dateTime } from '../lib/format';
import { Button, Card, ErrorText } from '../components/ui';

interface Attachment {
  id: number;
  fileName: string | null;
  mime: string;
  bytes: number;
  createdAt: string;
  hasPreview: boolean;
}

interface JobPackage {
  to: string[];
  forms: { form: string; label: string }[];
  attachments: { id: number; fileName: string | null }[];
  tooBig: { id: number; fileName: string | null }[];
  sent: { id: number; to: string; status: 'QUEUED' | 'SENT' | 'FAILED'; sentAt: string | null; createdAt: string; error: string | null }[];
}

const MAX_BYTES = 25 * 1024 * 1024;
const size = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function AttachmentsTab({ woId, readOnly, onChange }: { woId: number; readOnly: boolean; onChange: () => void }) {
  const q = useQuery({ queryKey: ['wo-attachments', woId], queryFn: () => api.get<Attachment[]>(`/api/work-orders/${woId}/attachments`) });
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (files: FileList) => {
    setError(null);
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        if (f.size > MAX_BYTES) throw new Error(`${f.name} is larger than 25 MB`);
        const res = await fetch(`/api/work-orders/${woId}/attachments`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': f.type || 'application/octet-stream', 'x-file-name': encodeURIComponent(f.name) },
          body: f,
        });
        if (!res.ok) throw new Error(((await res.json().catch(() => null)) as { message?: string } | null)?.message ?? `Upload failed (${res.status})`);
      }
      await q.refetch();
      onChange();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const list = q.data ?? [];
  return (
    <Card
      title={`Attachments (${list.length})`}
      actions={!readOnly && <Button variant="secondary" disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Uploading…' : 'Add PDF or scan'}</Button>}
    >
      <input ref={input} type="file" multiple accept="application/pdf,image/jpeg,image/png,image/heic,image/heif,.pdf,.jpg,.jpeg,.png,.heic" className="hidden" onChange={(e) => {
        if (e.target.files?.length) void upload(e.target.files);
        e.target.value = '';
      }} />
      <ErrorText error={error} />
      {list.length === 0 && <p className="text-sm text-slate-500">No attachments. Add vendor invoices, signed paper forms or other scans here — they go to billing with the closed-job package.</p>}
      <ul className="divide-y text-sm">
        {list.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-2 py-2">
            <span className="mr-auto font-medium">{a.fileName ?? `Attachment #${a.id}`}</span>
            <span className="text-xs text-slate-500">{a.mime === 'application/pdf' ? 'PDF' : 'Image'} · {size(a.bytes)} · {dateTime(a.createdAt)}</span>
            <a className="text-blue-700 underline" href={`/api/files/${a.id}${a.hasPreview ? '' : '?original=1'}`} target="_blank" rel="noreferrer">Open</a>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-slate-500">PDF, JPG, PNG or HEIC, up to 25 MB each. Files can't be removed or replaced once added. Mechanics don't see them. A scan never counts for a close check.</p>
    </Card>
  );
}

/** What the closed-job package carries, and (Manager) send it again. */
export function JobPackageCard({ woId, status, me }: { woId: number; status: string; me: Me }) {
  const q = useQuery({ queryKey: ['wo-job-package', woId], queryFn: () => api.get<JobPackage>(`/api/work-orders/${woId}/job-package`) });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isManager = me.role === 'ADMIN' || me.role === 'MANAGER';
  if (!q.data) return null;
  const p = q.data;
  const resend = async () => {
    setError(null);
    setBusy(true);
    try {
      await api.post(`/api/work-orders/${woId}/job-package`);
      await q.refetch();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Job package to billing">
      <p className="text-xs text-slate-600">
        {p.to.length ? <>Sent automatically to <b>{p.to.join(', ')}</b> when the job closes.</> : 'No billing address is set (Admin → Email), so nothing is sent.'}
      </p>
      <ul className="mt-2 list-disc pl-5 text-sm">
        {p.forms.map((f) => <li key={f.form}>{f.label}</li>)}
        {p.attachments.map((a) => <li key={a.id}>{a.fileName ?? `Attachment #${a.id}`}</li>)}
        {p.tooBig.map((a) => <li key={a.id} className="text-amber-800">{a.fileName ?? `Attachment #${a.id}`} (too large to email)</li>)}
        <li>Office-only notes (in the email)</li>
      </ul>
      {p.sent.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-slate-600">
          {p.sent.map((s) => (
            <li key={s.id}>
              {s.status === 'SENT' ? `Sent ${dateTime(s.sentAt)}` : s.status === 'FAILED' ? 'Failed' : 'Waiting to send'} · {s.to}
              {s.error && s.status !== 'SENT' && <span className="block text-red-700">{s.error}</span>}
            </li>
          ))}
        </ul>
      )}
      <ErrorText error={error} />
      {isManager && status === 'CLOSED' && p.to.length > 0 && (
        <Button variant="secondary" className="mt-2" disabled={busy} onClick={resend}>{p.sent.length ? 'Send to billing again' : 'Send to billing'}</Button>
      )}
    </Card>
  );
}
